import React, { useMemo, useState } from "react";
import "./styles/UsageWidget.css";
import { useUsage } from "../providers/UsageProvider";
import { Plan } from "../services/UserService";

const UsageWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const usage = useUsage();
  const usedMinutes = useMemo(() => usage.limits?.used ?? 0, [usage.limits]);
  const maxMinutes = useMemo(() => usage.limits?.maxUsage ?? 0, [usage.limits]);
  const usedApiRequests = useMemo(() => usage.limits?.usedApiRequests ?? 0, [usage.limits]);
  const maxApiRequests = useMemo(() => usage.limits?.maxApiRequests ?? 0, [usage.limits]);
  const plan = useMemo(() => usage.limits?.plan, [usage.limits]);
  const resetsAfter = useMemo(() => usage.limits?.resetsAfter ?? null, [usage.limits]);

  const toggleOpen = () => setIsOpen(prev => !prev);

  const percent = (used: number, max: number) =>
    !max ? 0 : Math.min(100, Math.round((used / max) * 100));

  return (
    <>
      {/* Floating icon toggle – always visible */}
      <button
        type="button"
        className="usage-widget-toggle"
        onClick={toggleOpen}
        aria-label={isOpen ? "Hide usage panel" : "Show usage panel"}
      >
        📊
      </button>

      {/* Floating panel – expanded view */}
      <div className={`usage-widget-panel ${isOpen ? "usage-widget-panel--open" : ""}`}>
        <div className="usage-widget-panel__header">
          <span className="usage-widget-panel__title">
            Usage (this month)
          </span>
          <button
            type="button"
            className="usage-widget-panel__close"
            onClick={toggleOpen}
            aria-label="Close usage panel"
          >
            ×
          </button>
        </div>

        <div className="usage-widget-panel__body">
          <div className="usage-widget-row">
            <div className="usage-widget-row__top">
              <span className="usage-widget-row__label">
                Processing minutes
              </span>
              <span className="usage-widget-row__value">
                {usedMinutes} / {maxMinutes}
              </span>
            </div>
            <div className="usage-widget-row__bar">
              <div
                className="usage-widget-row__bar-fill"
                style={{ width: `${percent(usedMinutes, maxMinutes)}%` }}
              />
            </div>
          </div>
        </div>

        {plan === Plan.Pro || plan === Plan.Developer ?
          <div className="usage-widget-panel__body">
            <div className="usage-widget-row">
              <div className="usage-widget-row__top">
                <span className="usage-widget-row__label">
                  API requests
                </span>
                <span className="usage-widget-row__value">
                  {usedApiRequests} / {maxApiRequests}
                </span>
              </div>
              <div className="usage-widget-row__bar">
                <div
                  className="usage-widget-row__bar-fill"
                  style={{ width: `${percent(usedApiRequests, maxApiRequests)}%` }}
                />
              </div>
            </div>
          </div> : <></>}

        {resetsAfter && (
          <div className="usage-widget-panel__footer">
            Resets in {resetsAfter} days
          </div>
        )}
      </div>
    </>
  );
};

export default UsageWidget;
